import { useCallback } from "react"
import { BigNumber } from "ethers"
import { commify } from "ethers/lib/utils"
import { useQueryClient } from "@tanstack/react-query"
import { useActiveWeb3React } from "src/hooks"
import { useTransactionModal } from "src/providers/TransactionModalProvider"
import {
  useFerroFarmBoostContract,
  useCronosCruiserContract,
} from "src/hooks/useContract"
import { FER } from "src/constants"
import { formatBNToString } from "src/utils"
import { BoostFarmQuery, FETCH_FARM_BOOST_DATA } from "src/constants/queryKeys"
import { enqueueToast } from "src/components/Toastify"

const useFarmBoostHandler = () => {
  const ferroFarmBoostContract = useFerroFarmBoostContract()
  const nftContract = useCronosCruiserContract()
  const { addTransactions } = useTransactionModal()
  const { account } = useActiveWeb3React()
  const queryClient = useQueryClient()

  const invalidFarmBoostData = useCallback(() => {
    void queryClient.invalidateQueries([FETCH_FARM_BOOST_DATA])
  }, [queryClient])

  const invalidateQueries = useCallback(() => {
    void queryClient.invalidateQueries([
      BoostFarmQuery.FARM_BOOST_CALCULATE_TOTAL_MULTIPLIER,
    ])
    void queryClient.invalidateQueries([BoostFarmQuery.GET_BOOSTED_FARMS])
    void queryClient.invalidateQueries([FETCH_FARM_BOOST_DATA])
  }, [queryClient])

  const invalidateHarvestQueries = useCallback(() => {
    void queryClient.invalidateQueries([BoostFarmQuery.HARVEST_BOOST_BY_PID])
    void queryClient.invalidateQueries([BoostFarmQuery.HARVEST_ALL_BOOST])
  }, [queryClient])

  const handleBoostFarm = useCallback(
    async (pids: number[]) => {
      if (!ferroFarmBoostContract || !account) return

      const summary = [{ label: "Pool Boosting", customElement: null }]
      const boostFarmTx = () => {
        return ferroFarmBoostContract.boostFarms(pids)
      }
      try {
        await addTransactions([boostFarmTx], summary, true)
        invalidateQueries()
      } catch (e) {
        console.error(e)
        enqueueToast(
          "error",
          e instanceof Error ? e.message : "Fail to boost pools",
          "Failed",
        )
      }
    },
    [account, addTransactions, ferroFarmBoostContract, invalidateQueries],
  )

  const handleRemoveBoost = useCallback(
    async (pid: number) => {
      if (!ferroFarmBoostContract || !account) return

      const summary = [{ label: "Remove Pool Boost", customElement: null }]
      const removeBoostTx = () => {
        return ferroFarmBoostContract.removeBoost(pid)
      }
      try {
        await addTransactions([removeBoostTx], summary, true)
        invalidateQueries()
      } catch (e) {
        console.error(e)
        enqueueToast(
          "error",
          e instanceof Error ? e.message : "Fail to remove the boosted pool",
          "Failed",
        )
      }
    },
    [account, addTransactions, ferroFarmBoostContract, invalidateQueries],
  )

  const handleHarvestBoost = useCallback(
    async (pid?: number | null, amount?: BigNumber) => {
      if (!ferroFarmBoostContract || !account || !pid) return

      const summary = [
        {
          label: `Claim`,
          token: FER,
          amount: amount
            ? commify(formatBNToString(amount, FER.decimals, 2))
            : "",
        },
      ]
      const harvestBoostTx = () => {
        return ferroFarmBoostContract.harvestBoost(pid)
      }
      try {
        await addTransactions([harvestBoostTx], summary, true)
        invalidateHarvestQueries()
      } catch (e) {
        console.error(e)
        enqueueToast(
          "error",
          e instanceof Error ? e.message : "Fail to harvest boosted rewards",
          "Failed",
        )
        throw e
      }
    },
    [
      account,
      addTransactions,
      ferroFarmBoostContract,
      invalidateHarvestQueries,
    ],
  )

  const handleHarvestAllBoost = useCallback(
    async (amount?: BigNumber) => {
      if (!ferroFarmBoostContract || !account) return

      const summary = [
        {
          label: `Claim`,
          token: FER,
          amount: amount
            ? commify(formatBNToString(amount, FER.decimals, 2))
            : "",
        },
      ]
      const harvestBoostTx = () => {
        return ferroFarmBoostContract.harvestAllBoost()
      }
      try {
        await addTransactions([harvestBoostTx], summary, true)
        invalidateHarvestQueries()
      } catch (e) {
        console.error(e)
        enqueueToast(
          "error",
          e instanceof Error ? e.message : "Fail to harvest boosted rewards",
          "Failed",
        )
        throw e
      }
    },
    [
      account,
      addTransactions,
      ferroFarmBoostContract,
      invalidateHarvestQueries,
    ],
  )

  const handleStakeNft = useCallback(
    async (newIds: string[], oldIds: string[]) => {
      if (!ferroFarmBoostContract || !account || !nftContract) return

      const isApproved = await nftContract.isApprovedForAll(
        account,
        ferroFarmBoostContract.address,
      )
      const txs = []
      if (!isApproved) {
        const approveTx = () => {
          return nftContract.setApprovalForAll(
            ferroFarmBoostContract.address,
            true,
          )
        }
        txs.push(approveTx)
      }

      const unstakeIds = oldIds.filter((id) => !newIds.includes(id))
      const stakeIds = newIds.filter((id) => !oldIds.includes(id))

      if (unstakeIds.length > 0) {
        const unstakeNftsParams = unstakeIds.map((tokenId) => {
          return {
            tokenContract: nftContract.address,
            tokenId,
          }
        })

        const unstakeNftTx = () => {
          return ferroFarmBoostContract.unstakeNfts(unstakeNftsParams)
        }
        txs.push(unstakeNftTx)
      }
      if (stakeIds.length > 0) {
        const stakeNftsParams = stakeIds.map((tokenId) => {
          return {
            tokenContract: nftContract.address,
            tokenId,
          }
        })

        const stakeNftTx = () => {
          return ferroFarmBoostContract.stakeNfts(stakeNftsParams)
        }
        txs.push(stakeNftTx)
      }
      const summary = [
        {
          label: stakeIds.length > 0 ? "NFT Staking" : "NFT Unstaking",
          customElement: null,
        },
      ]
      try {
        await addTransactions(txs, summary, true)
        void queryClient.invalidateQueries([
          BoostFarmQuery.FARM_BOOST_CALCULATE_TOTAL_MULTIPLIER,
        ])
        void queryClient.invalidateQueries([BoostFarmQuery.GET_BOOSTED_NFTS])
        void queryClient.invalidateQueries([FETCH_FARM_BOOST_DATA])
      } catch (e) {
        console.error(e)
        enqueueToast(
          "error",
          e instanceof Error ? e.message : "Fail to update NFT staking",
          "Failed",
        )
      }
    },
    [
      account,
      addTransactions,
      ferroFarmBoostContract,
      nftContract,
      queryClient,
    ],
  )

  return {
    onBoostFarm: handleBoostFarm,
    onRemoveBoost: handleRemoveBoost,
    onHarvestBoost: handleHarvestBoost,
    onHarvestAllBoost: handleHarvestAllBoost,
    onStakeNft: handleStakeNft,
    onInvalidFarmBoostData: invalidFarmBoostData,
  }
}

export const useClaimBoostRewards = (
  amount: BigNumber,
  pid?: number | null,
) => {
  const { onHarvestBoost } = useFarmBoostHandler()

  const wrappedClaim = async () => {
    await onHarvestBoost(pid, amount)
  }

  return {
    claim: wrappedClaim,
  }
}

export const useClaimAllBoostRewards = (amount?: BigNumber) => {
  const { onHarvestAllBoost } = useFarmBoostHandler()

  const wrappedClaim = async () => {
    await onHarvestAllBoost(amount)
  }

  return {
    claim: wrappedClaim,
  }
}

export default useFarmBoostHandler
